/**
 * @ipc/admin-ui · shared/ui · form-drawer —
 * Адаптивный контейнер форм сущностей: desktop — Sheet справа,
 * mobile (< sm) — vaul Drawer снизу (grabber + свайп).
 * API близко к Dialog/Sheet: Root + Content + Header/Title/Description/Footer/Trigger.
 */
import * as React from 'react';
import { cn, useIsMobile } from '@/shared/lib';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerTitle,
  DrawerTrigger,
} from './drawer';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetTitle,
  SheetTrigger,
} from './sheet';

type FormDrawerSize = 'md' | 'lg';

const FormDrawerContext = React.createContext<{ isMobile: boolean }>({ isMobile: false });

function useFormDrawerContext(): { isMobile: boolean } {
  return React.useContext(FormDrawerContext);
}

function FormDrawer({
  open,
  onOpenChange,
  children,
}: {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  children?: React.ReactNode;
}) {
  const isMobile = useIsMobile();

  return (
    <FormDrawerContext.Provider value={{ isMobile }}>
      {isMobile ? (
        <Drawer open={open} onOpenChange={onOpenChange}>
          {children}
        </Drawer>
      ) : (
        <Sheet open={open} onOpenChange={onOpenChange}>
          {children}
        </Sheet>
      )}
    </FormDrawerContext.Provider>
  );
}

function FormDrawerTrigger({
  ...props
}: React.ComponentProps<typeof SheetTrigger>) {
  const { isMobile } = useFormDrawerContext();
  if (isMobile) {
    return <DrawerTrigger data-slot="form-drawer-trigger" {...props} />;
  }
  return <SheetTrigger data-slot="form-drawer-trigger" {...props} />;
}

function FormDrawerClose({
  ...props
}: React.ComponentProps<typeof SheetClose>) {
  const { isMobile } = useFormDrawerContext();
  if (isMobile) {
    return <DrawerClose data-slot="form-drawer-close" {...props} />;
  }
  return <SheetClose data-slot="form-drawer-close" {...props} />;
}

/** Ширина island-drawer: перебивает дефолт SheetContent (24rem). */
const sizeClass: Record<FormDrawerSize, string> = {
  md: 'w-[min(100%-1.5rem,28rem)] sm:max-w-md',
  lg: 'w-[min(100%-1.5rem,42rem)] sm:max-w-2xl',
};

function FormDrawerContent({
  className,
  children,
  title,
  size = 'md',
}: {
  className?: string;
  children?: React.ReactNode;
  title?: string;
  size?: FormDrawerSize;
}) {
  const { isMobile } = useFormDrawerContext();

  if (isMobile) {
    return (
      <DrawerContent
        data-slot="form-drawer-content"
        className={cn('flex flex-col gap-0 overflow-y-auto px-4 pb-6 pt-2', className)}
      >
        {children}
      </DrawerContent>
    );
  }

  return (
    <SheetContent
      data-slot="form-drawer-content"
      side="right"
      title={title}
      className={cn(
        'flex flex-col gap-0 overflow-y-auto',
        sizeClass[size],
        className,
      )}
    >
      {children}
    </SheetContent>
  );
}

function FormDrawerHeader({
  className,
  ...props
}: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="form-drawer-header"
      className={cn('flex flex-col gap-1.5 pb-2', className)}
      {...props}
    />
  );
}

function FormDrawerFooter({
  className,
  ...props
}: React.ComponentProps<'div'>) {
  const { isMobile } = useFormDrawerContext();
  if (isMobile) {
    return (
      <div
        data-slot="form-drawer-footer"
        className={cn(
          'mt-auto flex flex-col-reverse gap-2 border-t border-border pt-4 sm:flex-row sm:justify-end',
          className,
        )}
        {...props}
      />
    );
  }
  return (
    <SheetFooter
      data-slot="form-drawer-footer"
      className={cn('mt-auto flex flex-col gap-2 pt-2 sm:flex-row sm:justify-end', className)}
      {...props}
    />
  );
}

function FormDrawerTitle({
  className,
  ...props
}: React.ComponentProps<typeof SheetTitle>) {
  const { isMobile } = useFormDrawerContext();
  if (isMobile) {
    return (
      <DrawerTitle
        data-slot="form-drawer-title"
        className={cn('text-foreground font-semibold', className)}
        {...props}
      />
    );
  }
  return (
    <SheetTitle
      data-slot="form-drawer-title"
      className={cn('text-foreground font-semibold', className)}
      {...props}
    />
  );
}

function FormDrawerDescription({
  className,
  ...props
}: React.ComponentProps<typeof SheetDescription>) {
  const { isMobile } = useFormDrawerContext();
  if (isMobile) {
    return (
      <DrawerDescription
        data-slot="form-drawer-description"
        className={cn('text-muted-foreground text-sm', className)}
        {...props}
      />
    );
  }
  return (
    <SheetDescription
      data-slot="form-drawer-description"
      className={cn('text-muted-foreground text-sm', className)}
      {...props}
    />
  );
}

/** Обёртка body формы: скролл + отступы между header и footer. */
function FormDrawerBody({
  className,
  ...props
}: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="form-drawer-body"
      className={cn('flex flex-1 flex-col gap-4 py-4', className)}
      {...props}
    />
  );
}

export {
  FormDrawer,
  FormDrawerTrigger,
  FormDrawerClose,
  FormDrawerContent,
  FormDrawerHeader,
  FormDrawerFooter,
  FormDrawerTitle,
  FormDrawerDescription,
  FormDrawerBody,
};
export type { FormDrawerSize };
